import React, { useEffect, useMemo, useState } from 'react';
import client from '../../../lib/feathers';

// The shared <Modal> component is hard-capped at sm:max-w-md which is
// way too narrow for the per-vintage plan table. We use a custom inline
// overlay here with the same dark-backdrop pattern so it visually
// matches the rest of the admin but accommodates a wide table.

const ACTION_LABEL = {
  move: 'Move (re-point libationId)',
  merge_into: 'Merge into keeper LV',
  keep_keeper_only: 'Keeper-only year',
  keep_donor: 'Keep donor LV (drop keeper LV)',
};

function ActionRow({ row, decision, onChangeDecision }) {
  const isConflict = !!row.donorLv && !!row.keeperLv;
  const conflictRisk = row.riskNotes && row.riskNotes.length > 0;
  const donorHasData = row.donorLv && (row.donorLv.sips + row.donorLv.cellarEntries + row.donorLv.scans + row.donorLv.flightEntries) > 0;
  const keeperHasData = row.keeperLv && (row.keeperLv.sips + row.keeperLv.cellarEntries + row.keeperLv.scans + row.keeperLv.flightEntries) > 0;

  return (
    <tr className={conflictRisk ? 'bg-amber-50' : ''}>
      <td className="px-3 py-2 text-sm font-mono">{row.year ?? 'NV'}</td>
      <td className="px-3 py-2 text-sm">
        {row.donorLv ? (
          <div>
            <code className="text-xs">{row.donorLv.id.slice(0, 8)}</code>
            <div className={`text-xs ${donorHasData ? 'text-amber-700' : 'text-gray-400'}`}>
              sips {row.donorLv.sips} · cellar {row.donorLv.cellarEntries} · scans {row.donorLv.scans} · flights {row.donorLv.flightEntries} · formats {row.donorLv.formats}
            </div>
          </div>
        ) : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-2 text-sm">
        {row.keeperLv ? (
          <div>
            <code className="text-xs">{row.keeperLv.id.slice(0, 8)}</code>
            <div className={`text-xs ${keeperHasData ? 'text-amber-700' : 'text-gray-400'}`}>
              sips {row.keeperLv.sips} · cellar {row.keeperLv.cellarEntries} · scans {row.keeperLv.scans} · flights {row.keeperLv.flightEntries} · formats {row.keeperLv.formats}
            </div>
          </div>
        ) : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-2 text-sm">
        {row.action === 'keep_keeper_only' ? (
          <span className="text-gray-500 italic">{ACTION_LABEL[row.action]}</span>
        ) : isConflict ? (
          <div className="space-y-1">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`action-${row.donorLv.id}`}
                checked={(decision || row.action) === 'merge_into'}
                onChange={() => onChangeDecision(row.donorLv.id, 'merge_into')}
              />
              <span>Merge into keeper LV (move donor user data, drop donor LV)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`action-${row.donorLv.id}`}
                checked={(decision || row.action) === 'move'}
                onChange={() => onChangeDecision(row.donorLv.id, 'move')}
              />
              <span>Move donor LV (keep both LVs; donor LV gets re-pointed)</span>
            </label>
          </div>
        ) : (
          <span className="text-gray-500">{ACTION_LABEL[row.action]}</span>
        )}
      </td>
      <td className="px-3 py-2 text-sm">
        {row.riskNotes && row.riskNotes.length > 0 ? (
          <ul className="text-xs text-amber-800 list-disc list-inside">
            {row.riskNotes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        ) : <span className="text-gray-300 text-xs">no risk</span>}
      </td>
    </tr>
  );
}

export default function LibationMergeModal({ open, onClose, onComplete, libations }) {
  const [donorId, setDonorId] = useState('');
  const [keeperId, setKeeperId] = useState('');
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState(null);

  useEffect(() => {
    if (!open) {
      setDonorId(''); setKeeperId(''); setPlan(null); setDecisions({});
      setPlanError(null); setExecuteError(null);
    }
  }, [open]);

  const candidates = useMemo(() => (libations || []).map((l) => ({ id: l.id, name: l.name })), [libations]);

  const swap = () => {
    setDonorId(keeperId);
    setKeeperId(donorId);
    setPlan(null);
    setDecisions({});
  };

  const runPlan = async () => {
    setPlanning(true);
    setPlanError(null);
    setPlan(null);
    setDecisions({});
    try {
      const result = await client.service('admin/libation-merge-plan').create({ donorId, keeperId });
      setPlan(result);
    } catch (e) {
      setPlanError(e.message || 'Plan failed');
    } finally {
      setPlanning(false);
    }
  };

  const execute = async () => {
    if (!plan) return;
    if (!window.confirm('Execute this merge? This action cannot be undone.')) return;
    setExecuting(true);
    setExecuteError(null);
    try {
      await client.service('admin/libation-merge').update(
        donorId,
        { id: keeperId, vintageDecisions: decisions }
      );
      onComplete && onComplete();
      onClose();
    } catch (e) {
      setExecuteError(e.message || 'Merge failed');
    } finally {
      setExecuting(false);
    }
  };

  const canPlan = donorId && keeperId && donorId !== keeperId;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose} />
      <div className="relative max-w-5xl mx-auto my-12 bg-white rounded-lg shadow-xl p-6">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg font-medium text-gray-900">Review Libation Merge</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        <div className="space-y-4 text-sm">
        <p className="text-gray-600">
          Pick the donor (the wine you want to remove) and the keeper (the canonical wine).
          Donor's name + aliases get appended to keeper. Per-vintage actions can be reviewed before executing.
        </p>

        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="block text-xs uppercase text-gray-500 mb-1">Donor (will be merged away)</label>
            <select value={donorId} onChange={(e) => { setDonorId(e.target.value); setPlan(null); }} className="w-full border rounded px-2 py-1">
              <option value="">— select —</option>
              {candidates.map((c) => <option key={c.id} value={c.id} disabled={c.id === keeperId}>{c.name}</option>)}
            </select>
          </div>
          <button type="button" onClick={swap} className="self-end text-xs px-2 py-1 border rounded hover:bg-gray-50" title="Swap">⇄</button>
          <div className="flex-1">
            <label className="block text-xs uppercase text-gray-500 mb-1">Keeper (canonical)</label>
            <select value={keeperId} onChange={(e) => { setKeeperId(e.target.value); setPlan(null); }} className="w-full border rounded px-2 py-1">
              <option value="">— select —</option>
              {candidates.map((c) => <option key={c.id} value={c.id} disabled={c.id === donorId}>{c.name}</option>)}
            </select>
          </div>
          <button
            type="button"
            onClick={runPlan}
            disabled={!canPlan || planning}
            className="self-end px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
          >
            {planning ? 'Planning…' : 'Plan'}
          </button>
        </div>

        {planError && <div className="text-red-700">{planError}</div>}

        {plan && (
          <div className="space-y-3 border-t border-gray-200 pt-3">
            {plan.warnings && plan.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <div className="font-medium text-amber-900 mb-1">Warnings</div>
                <ul className="list-disc list-inside text-amber-800 text-xs">
                  {plan.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="text-xs">
              <span className="font-medium">Alias write: </span>
              {plan.aliasWrite.willWrite
                ? <span className="text-green-700">"{plan.aliasWrite.alias}" → keeper.aliases</span>
                : <span className="text-gray-500 italic">none (already on keeper)</span>}
            </div>

            {plan.vintagePlan.length === 0 ? (
              <div className="text-gray-500 italic">No vintages on either libation.</div>
            ) : (
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Year</th>
                    <th className="px-3 py-2">Donor LV</th>
                    <th className="px-3 py-2">Keeper LV</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Risk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {plan.vintagePlan.map((row, idx) => (
                    <ActionRow
                      key={row.donorLv?.id || row.keeperLv?.id || idx}
                      row={row}
                      decision={row.donorLv ? decisions[row.donorLv.id] : undefined}
                      onChangeDecision={(lvId, action) =>
                        setDecisions((d) => ({ ...d, [lvId]: action }))
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}

            {executeError && <div className="text-red-700">{executeError}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-1 border rounded">Cancel</button>
              <button
                type="button"
                onClick={execute}
                disabled={executing}
                className="px-3 py-1 bg-red-700 text-white rounded disabled:opacity-50"
              >
                {executing ? 'Executing…' : 'Execute merge'}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
